import { Injectable } from '@angular/core';
import { TitleStrategy, RouterStateSnapshot } from '@angular/router';
import { Title } from '@angular/platform-browser';

/**
 * Browser-tab title strategy: `{panel} - F2 Admin`.
 *
 * Each child route declares its own `title` (the panel name).
 * This strategy walks to the deepest route with a `title` set and
 * appends ` - F2 Admin`. Routes without a title fall back to plain
 * `F2 Admin` (e.g. /home, /login).
 *
 * Mike asked for this 2026-06-25 — when an item is selected on the
 * left nav, the browser tab should read e.g. "Users - F2 Admin",
 * "Error Reports - F2 Admin", "Customer Directory - F2 Admin".
 *
 * UNREAD BADGE PREFIX (Mike 2026-07-03):
 *   When any sidenav badge (Error Reports / Commerce Events unread
 *   count) is > 0, prepend `♪N ` to the title where N is the LARGEST
 *   of the badge counts. Server-side cap is 10 so N renders as
 *   '1'..'9' or '9+'. The music-note glyph signals "something's
 *   making noise" while the number carries the volume — mirrors the
 *   sidenav pill visually.
 *
 *   DI CYCLE NOTE: this strategy MUST NOT inject Router or any service
 *   whose constructor injects Router — the router pipeline itself
 *   injects TitleStrategy, so introducing that dependency deadlocks
 *   the SPA at bootstrap (NG0200). Instead, AppComponent (which is
 *   OUTSIDE the router-pipeline injection chain) subscribes to
 *   UserPrefsService.state$() and pushes the current max unread into
 *   this strategy via setBadge(). setBadge triggers a re-title.
 */
@Injectable({ providedIn: 'root' })
export class AppTitleStrategy extends TitleStrategy {
  static readonly SUFFIX = 'F2 Admin';

  // Snapshot of the last routed panel body ('Users', 'Error Reports',
  // ...). Stored so setBadge-driven re-titles have a stable panel name
  // between navigations.
  private _lastPanel: string | null = null;
  // Current badge value. -1 sentinel = uninitialized (draw nothing);
  // 0 = no badge; > 0 = draw the prefix. Kept separate from the panel
  // cache so we can distinguish "no bump yet" from "0 unread".
  private _badgeMax: number = -1;

  constructor(private readonly title: Title) {
    super();
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this._lastPanel = this.buildTitle(snapshot) || null;
    this._writeTitle();
  }

  /**
   * Push the current max sidenav unread count. Called by AppComponent's
   * UserPrefsService.state$() subscriber. Triggers a re-title so the
   * badge prefix updates immediately.
   */
  setBadge(maxUnread: number): void {
    const clamped = Math.max(0, Math.floor(Number(maxUnread) || 0));
    if (clamped === this._badgeMax) return;
    this._badgeMax = clamped;
    this._writeTitle();
  }

  /**
   * Compose + apply the browser title. Prefix `♪N ` when the badge is
   * > 0. When we haven't been routed yet (no panel cached) we still
   * emit the plain suffix (+ prefix if applicable) so the tab always
   * has a title.
   */
  private _writeTitle(): void {
    const prefix = this._badgePrefix();
    const body = this._lastPanel
      ? `${this._lastPanel} - ${AppTitleStrategy.SUFFIX}`
      : AppTitleStrategy.SUFFIX;
    const next = prefix ? `${prefix}${body}` : body;
    if (this.title.getTitle() !== next) this.title.setTitle(next);
  }

  private _badgePrefix(): string {
    if (this._badgeMax <= 0) return '';
    // Server caps unread at 10 so the badge reads 1..9 / 9+ — mirror
    // the sidenav rendering exactly.
    const label = this._badgeMax >= 10 ? '9+' : String(this._badgeMax);
    return `♪${label} `;
  }
}
